// (c) chaehongee. Confidential and proprietary.

import type { AppDispatch } from '../../../store/store'
import {
    appendMessage,
    updateLastMessageChunk,
    updateLastMessageNodeIds,
    setLastMessagePending,
    clearAllPending,
    setIsProcessing,
    setInteractive,
    setContextUsage
} from '../../../store/slices/chatSlice'
import {
    setPreviewImageUrl,
    clearPreviewImage,
    setNodes as setSelectedNodes
} from '../../../store/slices/selectionSlice'
import { mergeSessionEntities } from '../../../store/slices/sessionSlice'
import { incrementUnread } from '../../../store/slices/sessionTabsSlice'
import { streamAgentProcess, type AgentStreamEvent } from '../../../services/agent'
import type { AgentProcessRequest } from 'models-ts/agent'
import { getNode } from '../../../services/node'
import type { MessageRole } from '../utils/types'
import { registerSessionStream, releaseSessionStream } from '../../../services/sessionStreams/SessionStreamRegistry'
import { getToolDisplayName } from '../utils/toolNames'

export class StreamService {
    static start(
        dispatch: AppDispatch,
        request: AgentProcessRequest,
        onRefreshSession: () => Promise<void>
    ) {
        const sessionId = request.session_id
        if (!sessionId) {
            return { cancel: () => { } }
        }

        dispatch(setIsProcessing({ sessionId, isProcessing: true }))

        let lastMsgWasBubble = false
        let pendingNodeIds: string[] = []

        const createBubble = (text: string, role: MessageRole, pending?: boolean, nodeIds?: string[]) => ({
            kind: 'bubble' as const, role, text, id: crypto.randomUUID(), timestamp: Date.now(), pending, nodeIds
        })
        const createNote = (text: string) => ({
            kind: 'note' as const, text, id: crypto.randomUUID(), timestamp: Date.now()
        })
        const createError = (text: string) => ({
            kind: 'error' as const, text, id: crypto.randomUUID(), timestamp: Date.now()
        })

        const ensureAssistantBubble = () => {
            if (!lastMsgWasBubble) {
                const nodeIds = pendingNodeIds.length > 0 ? [...pendingNodeIds] : undefined
                pendingNodeIds = []
                dispatch(incrementUnread({ sessionId, by: 1 }))
                dispatch(appendMessage({ sessionId, message: createBubble('', 'assistant', true, nodeIds) }))
                lastMsgWasBubble = true
            }
        }

        const handle = streamAgentProcess(request, (evt: AgentStreamEvent) => {
            switch (evt.type) {
                case 'text_start':
                    ensureAssistantBubble()
                    break
                case 'text':
                    ensureAssistantBubble()
                    dispatch(updateLastMessageChunk({ sessionId, chunk: evt.content || '' }))
                    break
                case 'context_usage':
                    dispatch(setContextUsage({
                        sessionId,
                        contextUsage: {
                            model: evt.model,
                            inputTokens: evt.input_tokens,
                            outputTokens: evt.output_tokens,
                            outputTokenLimit: evt.output_token_limit,
                            contextWindowLimit: evt.context_window_limit,
                        }
                    }))
                    break

                case 'error':
                    console.log('[StreamService] error:', evt.error)
                    dispatch(appendMessage({ sessionId, message: createError(evt.error) }))
                    lastMsgWasBubble = false
                    dispatch(setIsProcessing({ sessionId, isProcessing: false }))
                    dispatch(clearAllPending({ sessionId }))
                    releaseSessionStream(sessionId, handle)
                    break

                case 'status':
                    dispatch(appendMessage({ sessionId, message: createNote(`(${evt.step ?? 'status'}) ${evt.message}`) }))
                    if (lastMsgWasBubble) {
                        dispatch(setLastMessagePending({ sessionId, pending: false }))
                    }
                    lastMsgWasBubble = false
                    break

                case 'tool_use':
                    dispatch(appendMessage({
                        sessionId,
                        message: createNote(`calling tool: ${evt.tool_name}`)
                    }))
                    if (lastMsgWasBubble) {
                        dispatch(setLastMessagePending({ sessionId, pending: false }))
                    }
                    lastMsgWasBubble = false
                    break

                case 'interaction_required':
                    dispatch(setInteractive({
                        sessionId,
                        interactive: {
                            toolName: evt.tool_name,
                            toolId: evt.tool_id,
                            uiSpec: evt.ui_spec
                        }
                    }))
                    dispatch(appendMessage({ sessionId, message: createNote(`interaction required: ${getToolDisplayName(evt.tool_name)}`) }))
                    if (lastMsgWasBubble) {
                        dispatch(setLastMessagePending({ sessionId, pending: false }))
                    }
                    lastMsgWasBubble = false
                    break

                case 'preview':
                    dispatch(setPreviewImageUrl({ sessionId, previewImageUrl: evt.image_url }))
                    break

                case 'tool_result':
                    if ((evt as any).error_type === 'insufficient_credits') {
                        dispatch(appendMessage({
                            sessionId,
                            message: {
                                kind: 'error' as const,
                                text: (evt as any).error || 'Insufficient credits.',
                                id: crypto.randomUUID(),
                                timestamp: Date.now(),
                                errorType: 'insufficient_credits',
                            },
                        }))
                        lastMsgWasBubble = false
                        dispatch(setIsProcessing({ sessionId, isProcessing: false }))
                        dispatch(clearAllPending({ sessionId }))
                        releaseSessionStream(sessionId, handle)
                        break
                    }
                    if ((evt as any).is_error) {
                        const toolName = evt.tool_name || 'unknown'
                        const displayName = getToolDisplayName(toolName)
                        dispatch(appendMessage({
                            sessionId,
                            message: {
                                kind: 'error' as const,
                                text: displayName,
                                id: crypto.randomUUID(),
                                timestamp: Date.now(),
                                errorType: 'tool_error',
                                toolName,
                            },
                        }))
                        if (lastMsgWasBubble) {
                            dispatch(setLastMessagePending({ sessionId, pending: false }))
                        }
                        lastMsgWasBubble = false
                        window.dispatchEvent(new CustomEvent('tool:error', {
                            detail: { toolName, displayName, errorType: (evt as any).error_type },
                        }))
                        break
                    }
                    if (evt.node_id && request.session_id) {
                        const newNodeId = evt.node_id
                        onRefreshSession().then(() => {
                            dispatch(setSelectedNodes({ sessionId, nodeIds: [newNodeId] }))
                        })
                    }
                    break

                case 'image':
                    const nid = evt.image_node_id
                    if (nid) {
                        if (lastMsgWasBubble) {
                            dispatch(updateLastMessageNodeIds({ sessionId, nodeIds: [nid] }))
                        } else {
                            pendingNodeIds.push(nid)
                        }

                        getNode(nid)
                            .then((node) => {
                                dispatch(mergeSessionEntities({ sessionId, nodes: [node] }))
                                dispatch(setSelectedNodes({ sessionId, nodeIds: [nid] }))
                            })
                            .catch(() => { })
                    }
                    dispatch(clearPreviewImage({ sessionId }))
                    if (request.session_id) {
                        onRefreshSession()
                    }
                    dispatch(setIsProcessing({ sessionId, isProcessing: false }))
                    releaseSessionStream(sessionId, handle)
                    break

                case 'done':
                    dispatch(setIsProcessing({ sessionId, isProcessing: false }))
                    dispatch(clearAllPending({ sessionId }))
                    releaseSessionStream(sessionId, handle)
                    break
            }
        })
        registerSessionStream(sessionId, handle)
        return handle
    }
}